<script setup>
  /* global defineEmits, defineProps */
  import { computed } from 'vue'
  import dayjs from 'dayjs'
  import ReplayQualityWarningList from './ReplayQualityWarningList.vue'
  import { formatReplayLanguage } from './replayLabels'

  const props = defineProps({
    task: { type: Object, default: null },
    recoverableCaptures: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  })

  const emit = defineEmits(['generate', 'recover-capture', 'refresh', 'retry-task', 'retry-output'])

  const statusLabels = {
    PENDING: '排队中',
    PROCESSING: '生产中',
    COMPLETED: '已完成',
    PARTIAL_FAILED: '部分失败',
    FAILED: '生产失败',
    CANCELLED: '已取消',
    SKIPPED: '已跳过',
    UNKNOWN: '状态未知',
  }

  const phaseLabels = {
    WAITING_FOR_DATA: '正在等待会议数据最终化',
    WAITING_DATA: '正在等待会议数据最终化',
    POLICY_PENDING: '生成策略暂未解析',
    AWAITING_MANUAL_REQUEST: '已就绪，等待手动生成',
    READY_TO_EXECUTE: '已请求生成，等待生产节点认领',
    QUEUED: '等待进入生产队列',
    RETRY_PENDING: '重试任务排队中',
    RECOVERY_RETRY: '恢复任务排队中',
    ORIGINAL_VIDEO: '正在生成原声成片',
    QUALITY_CHECK: '正在执行媒体质量门禁',
    COMPLETED: '全部产物生产完成',
    PARTIAL_FAILED: '部分语种生成失败',
    FAILED: '任务执行失败',
    QUEUE_REJECTED: '生产队列繁忙',
    INELIGIBLE: '采集数据不符合生成条件',
  }

  const generationLabels = {
    UNRESOLVED: '策略待解析',
    NOT_REQUESTED: '尚未请求',
    REQUESTED: '已请求',
    UNKNOWN: '未知',
  }

  const eligibilityLabels = {
    UNKNOWN: '待校验',
    WAITING: '数据最终化中',
    ELIGIBLE: '可生成',
    INELIGIBLE: '不可生成',
  }

  const sourceLabels = {
    AUTO: '会后自动生成',
    MANUAL: '手动生成',
    LEGACY: '历史触发',
  }

  const manualReasonLabels = {
    CAPTURE_SKIPPED: '该采集已跳过生成',
    CAPTURE_INELIGIBLE: '采集数据不符合生成条件',
    GENERATION_ALREADY_REQUESTED: '该采集已提交生成请求',
    GENERATION_STATE_UNAVAILABLE: '当前生成状态不可用',
    TASK_STATE_NOT_MANUAL_GENERATABLE: '当前任务状态不允许手动生成',
    USER_AUTHORIZATION_REQUIRED: '需要用户服务完成权限校验',
  }

  const statusLabel = computed(() => (props.task ? statusLabels[props.task.status] || '未知状态' : '暂无候选'))

  const statusTone = computed(() => {
    if (!props.task) return 'idle'
    if (props.task.status === 'COMPLETED') return 'success'
    if (['FAILED', 'PARTIAL_FAILED', 'SKIPPED'].includes(props.task.status)) return 'danger'
    if (['PENDING', 'PROCESSING'].includes(props.task.status)) return 'working'
    return 'idle'
  })

  const phaseLabel = computed(() => {
    const phase = props.task?.phase
    if (phase?.startsWith('TTS_')) return `正在生成 ${formatReplayLanguage(phase.slice(4))} 成片`
    if (phase?.startsWith('OUTPUT_RETRY_PENDING:')) return '单语种重试排队中'
    return phaseLabels[phase] || '等待任务状态'
  })

  const progress = computed(() => {
    const value = Number(props.task?.progress)
    return Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : 0
  })

  const completedCount = computed(
    () => props.task?.outputs?.filter((output) => output.status === 'COMPLETED' && output.videoUrl).length || 0
  )

  const canGenerate = computed(
    () => props.task?.canManualGenerate === true && !['COMPLETED', 'CANCELLED', 'SKIPPED'].includes(props.task?.status)
  )

  const canRetryTask = computed(
    () => props.task?.retryable === true && ['FAILED', 'PARTIAL_FAILED'].includes(props.task?.status)
  )

  const hasRecoverableCaptures = computed(
    () => !props.task && Array.isArray(props.recoverableCaptures) && props.recoverableCaptures.length > 0
  )

  const reasonItems = computed(() => {
    if (!props.task) return []
    const reasons = []
    const violations = Array.isArray(props.task.eligibilityViolations)
      ? props.task.eligibilityViolations.filter(Boolean)
      : []

    if (props.task.eligibilityCode || props.task.eligibilityMessage || violations.length) {
      reasons.push({
        key: 'eligibility',
        label: '资格判定',
        code: props.task.eligibilityCode || props.task.eligibilityStatus,
        message: [props.task.eligibilityMessage, ...violations].filter(Boolean).join('；'),
      })
    }
    if (props.task.errorCode || props.task.errorMessage) {
      reasons.push({
        key: 'task-error',
        label: '任务错误',
        code: props.task.errorCode || 'PRODUCTION_ERROR',
        message: props.task.errorMessage,
      })
    }
    if (props.task.canManualGenerate !== true && props.task.manualGenerationReasonCode) {
      reasons.push({
        key: 'manual-generation',
        label: '手动生成',
        code: props.task.manualGenerationReasonCode,
        message: manualReasonLabels[props.task.manualGenerationReasonCode] || '当前不允许手动生成',
      })
    }
    return reasons
  })

  const formatTime = (time, pattern = 'YYYY-MM-DD HH:mm:ss') => (time ? dayjs(time).format(pattern) : '—')

  const captureRange = computed(() => {
    const startedAt = props.task?.captureStartedAtMs
    const endedAt = props.task?.captureEndedAtMs
    if (!startedAt || !endedAt) return '—'
    const start = dayjs(startedAt)
    const end = dayjs(endedAt)
    if (!start.isValid() || !end.isValid()) return '—'
    const sameDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD')
    return `${start.format('YYYY-MM-DD HH:mm:ss')} — ${end.format(sameDay ? 'HH:mm:ss' : 'YYYY-MM-DD HH:mm:ss')}`
  })

  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.round(Number(milliseconds) / 1000))
    if (!Number.isFinite(totalSeconds) || totalSeconds === 0) return '—'
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return [hours, minutes, seconds]
      .map((value, index) => (index === 0 ? String(value) : String(value).padStart(2, '0')))
      .join(':')
  }

  const formatGenerationDuration = (milliseconds) => {
    if (milliseconds == null || milliseconds === '') return '—'
    const value = Number(milliseconds)
    if (!Number.isFinite(value) || value < 0) return '—'
    const totalSeconds = Math.max(0, Math.round(value / 1000))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':')
  }

  const captureRangeLabel = (capture) => {
    if (!capture?.captureStartedAtMs || !capture?.captureEndedAtMs) return '采集时间未知'
    const startedAt = dayjs(capture?.captureStartedAtMs)
    const endedAt = dayjs(capture?.captureEndedAtMs)
    if (!startedAt.isValid() || !endedAt.isValid()) return '采集时间未知'
    const sameDay = startedAt.format('YYYY-MM-DD') === endedAt.format('YYYY-MM-DD')
    return `${startedAt.format('YYYY-MM-DD HH:mm:ss')} — ${endedAt.format(
      sameDay ? 'HH:mm:ss' : 'YYYY-MM-DD HH:mm:ss'
    )}`
  }

  const captureLanguageLabel = (capture) => {
    const source = formatReplayLanguage(capture?.sourceLanguage, '')
    const targets = Array.isArray(capture?.requestedLanguages)
      ? capture.requestedLanguages.map((language) => formatReplayLanguage(language)).join(' / ')
      : ''
    const languages = targets ? `${source || '原声'} → ${targets}` : source || '仅原声'
    return [capture?.scheduleCode, languages].filter(Boolean).join(' · ')
  }

  const generationLabel = (state) => generationLabels[state] || state || '—'
  const eligibilityLabel = (status) => eligibilityLabels[status] || status || '—'
  const sourceLabel = (source) => sourceLabels[source] || source || '尚未触发'
  const outputLabel = (output) =>
    output.audioType === 'ORIGINAL' ? '原声版本' : `${formatReplayLanguage(output.language, '翻译')} 版本`
  const outputStatusLabel = (status) =>
    ({ PENDING: '待生产', PROCESSING: '生产中', COMPLETED: '可播放', FAILED: '生成失败' }[status] || status || '未知')
  const outputErrorText = (output) =>
    [output.errorCode || 'OUTPUT_ERROR', output.errorMessage].filter(Boolean).join('：')
  const outputTone = (status) => String(status || 'unknown').toLowerCase()
  const canRetryOutput = (output) =>
    canRetryTask.value && output?.id != null && output.status === 'FAILED' && output.audioType === 'TTS'

  const handleGenerate = () => {
    if (canGenerate.value) emit('generate')
  }

  const handleRetryTask = () => {
    if (canRetryTask.value) emit('retry-task')
  }

  const handleRetryOutput = (output) => {
    if (canRetryOutput(output)) emit('retry-output', output.id)
  }

  const handleRecoverCapture = (captureSessionId) => {
    if (!props.loading && captureSessionId) emit('recover-capture', captureSessionId)
  }
</script>

<template>
  <aside class="task-panel">
    <div class="panel-topline">
      <span class="eyebrow">PRODUCTION TASK</span>
      <span :class="['status-pill', statusTone]">{{ statusLabel }}</span>
    </div>

    <template v-if="task">
      <div class="task-identity">
        <strong>当前生产任务</strong>
      </div>

      <div class="progress-block">
        <div class="progress-meta">
          <span>{{ phaseLabel }}</span>
          <strong>{{ progress }}%</strong>
        </div>
        <el-progress :percentage="progress" :show-text="false" :stroke-width="6" />
      </div>

      <div aria-label="回放任务双轴状态" class="state-axes">
        <div>
          <span>数据资格</span>
          <strong>{{ eligibilityLabel(task.eligibilityStatus) }}</strong>
          <small>{{ task.eligibilityCode || '暂无原因码' }}</small>
        </div>
        <div>
          <span>生成请求</span>
          <strong>{{ generationLabel(task.generationRequestState) }}</strong>
          <small>{{ sourceLabel(task.generationRequestSource) }}</small>
        </div>
      </div>

      <dl class="task-meta">
        <div class="wide-meta">
          <dt>采集区间</dt>
          <dd>{{ captureRange }}</dd>
        </div>
        <div>
          <dt>目标时长</dt>
          <dd>{{ formatDuration(task.expectedDurationMs) }}</dd>
        </div>
        <div>
          <dt>{{ task.status === 'PROCESSING' ? '已耗时' : '生成耗时' }}</dt>
          <dd class="generation-duration">{{ formatGenerationDuration(task.generationDurationMs) }}</dd>
        </div>
        <div>
          <dt>创建时间</dt>
          <dd>{{ formatTime(task.createdAt, 'YYYY-MM-DD HH:mm') }}</dd>
        </div>
        <div>
          <dt>重试次数</dt>
          <dd>{{ task.retryCount || 0 }}</dd>
        </div>
        <div class="wide-meta">
          <dt>产物 / 可用</dt>
          <dd>{{ task.outputs?.length || 0 }} / {{ completedCount }}</dd>
        </div>
      </dl>

      <div v-if="reasonItems.length" class="reason-list">
        <div v-for="reason in reasonItems" :key="reason.key" class="reason-note">
          <span>{{ reason.label }}</span>
          <strong>{{ reason.code }}</strong>
          <small v-if="reason.message">{{ reason.message }}</small>
        </div>
      </div>

      <div v-if="task.outputs?.length" class="output-list">
        <div v-for="output in task.outputs" :key="output.id" class="output-row">
          <div class="output-detail">
            <span class="output-name">{{ outputLabel(output) }}</span>
            <small>{{ outputStatusLabel(output.status) }}</small>
            <small class="output-duration">生成耗时 {{ formatGenerationDuration(output.generationDurationMs) }}</small>
            <small v-if="output.errorCode || output.errorMessage" class="output-error">
              {{ outputErrorText(output) }}
            </small>
          </div>
          <button
            v-if="canRetryOutput(output)"
            class="inline-retry"
            :disabled="loading"
            type="button"
            @click="handleRetryOutput(output)"
          >
            重试
          </button>
          <span v-else :class="['output-dot', outputTone(output.status)]"></span>
        </div>
      </div>

      <ReplayQualityWarningList :outputs="task.outputs || []" />
    </template>

    <div v-else-if="hasRecoverableCaptures" class="recoverable-task">
      <strong>发现可补登记的采集数据</strong>
      <span>以下数据已完成原声、字幕和时间线最终化，可按精确 Capture 进入媒体质量门禁。</span>
      <div class="recoverable-list">
        <div v-for="capture in recoverableCaptures" :key="capture.captureSessionId" class="recoverable-item">
          <div>
            <small>{{ captureRangeLabel(capture) }}</small>
            <b>{{ formatDuration(capture.durationMs) }}</b>
            <small>{{ captureLanguageLabel(capture) }}</small>
          </div>
          <button :disabled="loading" type="button" @click="handleRecoverCapture(capture.captureSessionId)">
            使用此批数据
          </button>
        </div>
      </div>
    </div>

    <div v-else class="empty-task">
      <strong>暂无可生成的同传记录</strong>
      <span>仅展示具备完整最终化数据的精确采集会话，不会混用历史会议数据。</span>
    </div>

    <div class="task-actions">
      <el-button v-if="canGenerate" class="primary-action" :loading="loading" @click="handleGenerate">
        立即生成
      </el-button>
      <el-button v-if="canRetryTask" class="primary-action" :loading="loading" @click="handleRetryTask">
        重试失败任务
      </el-button>
      <el-button class="secondary-action" :disabled="loading" @click="emit('refresh')">刷新状态</el-button>
    </div>
  </aside>
</template>

<style lang="scss" scoped>
  .task-panel {
    padding: 24px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  }

  .panel-topline,
  .progress-meta,
  .output-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .eyebrow {
    color: var(--el-color-primary, #1890ff);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
  }

  .status-pill {
    padding: 5px 9px;
    color: #909399;
    background: #f5f7fa;
    border-radius: 999px;
    font-size: 11px;

    &.working {
      color: #b88200;
      background: #fdf6ec;
    }

    &.success {
      color: #13ce66;
      background: #f0f9eb;
    }

    &.danger {
      color: #ff4d4f;
      background: #fef0f0;
    }
  }

  .task-identity {
    display: flex;
    margin: 28px 0 24px;
    flex-direction: column;
    gap: 6px;

    strong {
      color: #303133;
      font-size: 18px;
    }
  }

  .progress-block {
    margin-bottom: 20px;
  }

  .progress-meta {
    margin-bottom: 10px;
    color: #606266;
    font-size: 12px;

    span {
      padding-right: 12px;
    }

    strong {
      color: #303133;
    }
  }

  :deep(.el-progress-bar__outer) {
    background: #ebeef5;
  }

  :deep(.el-progress-bar__inner) {
    background: var(--el-color-primary, #1890ff);
  }

  .state-axes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-bottom: 18px;
    background: #f7f9fc;
    border: 1px solid #ebeef5;
    border-radius: 6px;

    > div {
      display: flex;
      min-width: 0;
      padding: 12px;
      flex-direction: column;
      gap: 4px;

      + div {
        border-left: 1px solid #e4e7ed;
      }
    }

    span,
    small {
      color: #909399;
      font-size: 10px;
    }

    strong {
      color: #303133;
      font-size: 12px;
    }

    small {
      overflow-wrap: anywhere;
    }
  }

  .task-meta {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin: 0;
    padding: 18px 0;
    border-top: 1px solid #ebeef5;
    border-bottom: 1px solid #ebeef5;
    gap: 18px;

    div {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: 5px;
    }

    .wide-meta {
      grid-column: 1 / -1;
    }

    dt {
      color: #909399;
      font-size: 11px;
    }

    dd {
      margin: 0;
      color: #606266;
      font-size: 12px;
      overflow-wrap: anywhere;
    }

    .generation-duration {
      color: #303133;
      font-family: 'SFMono-Regular', Consolas, monospace;
      font-variant-numeric: tabular-nums;
    }
  }

  .reason-list {
    display: grid;
    margin-top: 16px;
    gap: 8px;
  }

  .reason-note {
    display: grid;
    padding: 10px 12px;
    color: #c45656;
    background: #fef0f0;
    border: 1px solid #fde2e2;
    border-radius: 6px;
    font-size: 11px;
    gap: 3px;

    span {
      color: #909399;
    }

    strong,
    small {
      overflow-wrap: anywhere;
    }
  }

  .output-list {
    max-height: 230px;
    margin-top: 16px;
    overflow: auto;
  }

  .output-row {
    padding: 10px 2px;
    border-bottom: 1px solid #ebeef5;
    gap: 8px;
  }

  .output-detail {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 3px;

    .output-name {
      color: #303133;
      font-size: 12px;
    }

    small {
      color: #909399;
    }

    .output-duration {
      color: #606266;
      font-family: 'SFMono-Regular', Consolas, monospace;
      font-variant-numeric: tabular-nums;
    }

    .output-error {
      color: #c45656;
      overflow-wrap: anywhere;
    }
  }

  .output-dot {
    width: 7px;
    height: 7px;
    flex: 0 0 7px;
    background: #c0c4cc;
    border-radius: 50%;

    &.completed {
      background: #13ce66;
    }

    &.processing {
      background: #ffba00;
    }

    &.failed {
      background: #ff4d4f;
    }
  }

  .inline-retry {
    padding: 4px 8px;
    color: #ff4d4f;
    background: transparent;
    border: 1px solid #fbc4c4;
    border-radius: 6px;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  .recoverable-task,
  .empty-task {
    display: flex;
    min-height: 260px;
    padding: 24px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    text-align: center;
    gap: 8px;

    strong {
      color: #606266;
      font-size: 15px;
    }

    span {
      max-width: 270px;
      color: #909399;
      font-size: 12px;
      line-height: 1.7;
    }
  }

  .recoverable-task {
    align-items: stretch;
    justify-content: flex-start;
    text-align: left;

    > span {
      max-width: none;
    }
  }

  .recoverable-list {
    display: grid;
    margin-top: 8px;
    gap: 10px;
  }

  .recoverable-item {
    display: flex;
    padding: 12px;
    align-items: center;
    justify-content: space-between;
    background: #f7f9fc;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    gap: 12px;

    > div {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: 4px;
    }

    b {
      color: #303133;
      font-family: 'SFMono-Regular', Consolas, monospace;
      font-size: 16px;
    }

    small {
      color: #909399;
      font-size: 10px;
      overflow-wrap: anywhere;
    }

    button {
      padding: 7px 10px;
      flex: 0 0 auto;
      color: #fff;
      background: var(--el-color-primary, #1890ff);
      border: 1px solid var(--el-color-primary, #1890ff);
      border-radius: 4px;
      cursor: pointer;

      &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
      }
    }
  }

  .task-actions {
    display: grid;
    margin-top: 20px;
    gap: 9px;

    .el-button {
      width: 100%;
      margin: 0;
      border-radius: 4px;
    }

    .primary-action {
      color: #fff;
      background: var(--el-color-primary, #1890ff);
      border-color: var(--el-color-primary, #1890ff);
    }

    .secondary-action {
      color: #606266;
      background: #fff;
      border-color: #dcdfe6;
    }
  }
</style>
